import { useEffect, useRef, useState } from 'react'
import type { DependencyList } from 'react'

type AsyncState<T> = {
  data: T | null
  error: Error | null
  loading: boolean
  reload: () => void
  /**
   * Loads again behind what is on screen: no loading state, and a failure keeps
   * the data already shown. For a page that should stay put after a small save.
   */
  refresh: () => void
}

export function useAsyncData<T>(
  loader: () => Promise<T>,
  deps: DependencyList = [],
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [loading, setLoading] = useState(true)
  const [version, setVersion] = useState(0)
  // Only the latest load may land, so a slow one cannot overwrite a newer one.
  const latestLoad = useRef(0)

  useEffect(() => {
    let cancelled = false
    const load = ++latestLoad.current

    setLoading(true)
    setError(null)

    loader()
      .then((result) => {
        if (!cancelled && load === latestLoad.current) {
          setData(result)
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setError(error instanceof Error ? error : new Error('Failed to load data'))
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
    // The caller supplies the data-loading key through deps; loader closures are intentionally scoped to that key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, version])

  function refresh() {
    const load = ++latestLoad.current
    loader()
      .then((result) => {
        if (load === latestLoad.current) setData(result)
      })
      .catch(() => {
        // Keep what is shown; the next full reload reports the failure.
      })
  }

  return {
    data,
    error,
    loading,
    reload: () => setVersion((current) => current + 1),
    refresh,
  }
}
